// 捲動跟隨狀態（#1334）。🔴 只有這一份狀態決定「要不要自動捲到底」：
// 使用者往上捲與展開工具紀錄是同一件事的兩個入口，分兩套必定互相打架。

/**
 * follow ＝ 黏在底部，新內容一長就跟上；
 * anchored ＝ 讓位給使用者，視窗維持不動（錨在 anchorId 那一則）。
 */
export type FollowMode = 'follow' | 'anchored'

export interface ScrollFollowState {
  readonly mode: FollowMode
  /** 錨定的那一則的 id。null ＝ 只是不自動捲，沒有特定要盯住的目標。 */
  readonly anchorId: string | null
  /** 目前展開中的工具紀錄。空了才代表「都看完了」。 */
  readonly expandedIds: readonly string[]
}

export const initialFollowState: ScrollFollowState = {
  mode: 'follow',
  anchorId: null,
  expandedIds: [],
}

/** 自動捲到底此刻是否仍生效。DOM 那一側只讀這一個判斷。 */
export const shouldStickToBottom = (state: ScrollFollowState): boolean => state.mode === 'follow'

// 展開＝「我要看這裡」。自動捲動立刻讓位，並錨在剛展開的那一則上。
const expand = (state: ScrollFollowState, id: string): ScrollFollowState => ({
  mode: 'anchored',
  anchorId: id,
  expandedIds: state.expandedIds.includes(id) ? state.expandedIds : [...state.expandedIds, id],
})

// 收合＝看完了。但只有「最後一個」收合才恢復跟隨，否則錨退回還開著的那一則。
const collapse = (state: ScrollFollowState, id: string): ScrollFollowState => {
  const expandedIds = state.expandedIds.filter((each) => each !== id)
  if (expandedIds.length === 0) return { mode: 'follow', anchorId: null, expandedIds }
  return { mode: 'anchored', anchorId: expandedIds[expandedIds.length - 1], expandedIds }
}

/** 工具紀錄被展開或收合。open 直接取 <details> 的 open。 */
export const onToolToggled = (
  state: ScrollFollowState,
  id: string,
  open: boolean,
): ScrollFollowState => (open ? expand(state, id) : collapse(state, id))

/**
 * 使用者捲動了。捲離底部就停止跟隨；捲回底部才恢復。
 * 🔴 還有東西展開著時捲回底部不恢復：展開只由收合解除，否則錨會被自己的
 * 程式化捲動彈掉，bug 立刻回來。
 */
export const onUserScrolled = (
  state: ScrollFollowState,
  atBottom: boolean,
): ScrollFollowState => {
  // 🔴 實質未變就回傳原物件（#1383）：換新物件會讓版面效果重跑、把位置校正回
  // 錨點，那次程式化捲動又觸發捲動事件，畫面就鎖死了。
  if (!atBottom) {
    return state.mode === 'anchored' ? state : { ...state, mode: 'anchored' }
  }
  if (state.expandedIds.length > 0) return state
  if (state.mode === 'follow' && state.anchorId === null) return state
  return { ...state, mode: 'follow', anchorId: null }
}

/**
 * 錨點換人時要重新量的那一則 id（null ＝ 沿用原本的偏移基準）。
 * 🔴 收合時錨會退回前一個展開項，基準不跟著換就是拿舊基準校正新錨點（#1383）。
 */
export const anchorToRemeasure = (
  before: ScrollFollowState,
  after: ScrollFollowState,
): string | null => (
  after.anchorId !== null && after.anchorId !== before.anchorId ? after.anchorId : null
)

/**
 * 整組工具的 anchor id。
 * 🔴 不能直接用組內第一張卡的 toolCallId（#1404）：撞號會讓「收合第一張卡」
 * 被當成「整組收好了」，於是恢復自動捲到底、畫面彈到底部。
 */
export const toolGroupAnchorId = (firstToolCallId: string): string =>
  `toolgroup:${firstToolCallId}`

/** 判定「到底了」的容忍值（px）。次像素與圓角邊界會讓它差個一兩點。 */
export const BOTTOM_SLACK = 24

/** 捲動容器是否已在底部。 */
export const isAtBottom = (
  metrics: { scrollTop: number; scrollHeight: number; clientHeight: number },
): boolean => metrics.scrollHeight - metrics.scrollTop - metrics.clientHeight <= BOTTOM_SLACK
